import { mapObjectKeysToValues } from '../../utils/core/object';

export const USER_FLAGS = {
    PAYMENT_ADMIN: 'payment_admin',
    CAN_VIEW_UNALLOCATED_CLAIMS: 'can_view_unallocated_claims',
} as const;

export const ORG_ROLES = {
    USER: 'user',
    USER_MANAGER: 'user_manager',
    ORG_ADMIN: 'org_admin',
    SUPPLIER: 'supplier',
    AUTO_DETECT_USER: 'auto_detect_user',
    SHARED_EDIT_USER: 'shared_edit_user',
    ATTESTATIONS_USER: 'attestations_user',
    WEBSITE_CHECKER_USER: 'website_checker_user',
    MARKET_PDS_USER: 'market_pds_user',
    TRAINING_MANAGER: 'training_manager',
    FLEET_PORTAL_USER: 'fleet_portal_user',
    /** Read-only fleet portal access across a consolidator's clients (CUR-1099). */
    FMC_COORDINATOR: 'fmc_coordinator',
    /** Full compliance audit access: create, run, answer, override and annotate audits. */
    COMPLIANCE_AUDIT_MANAGER: 'compliance_audit_manager',
    /** Read-only compliance audit access: view audits, evidence, insights and exports. */
    COMPLIANCE_AUDIT_READER: 'compliance_audit_reader',
    /**
     * Payment approval (CUR-2675, PAY-L2, A5, A9): sees every claim read only, gives the final
     * approval on payments and marks them paid. Assignable only with `enable-claim-payment-approval`.
     */
    ACCOUNTS: 'accounts',
    //CLAIM_ADMIN: 'claim_admin',
} as const;

export const ADMIN_ROLES = {
    SUPER_ADMIN: 'super_admin',
    // Read-only admin console role
    ADMIN_READ_ONLY: 'admin_read_only',
    // Granular admin console roles
    ADMIN_ORG_MANAGER: 'admin_org_manager',
    ADMIN_USER_MANAGER: 'admin_user_manager',
    ADMIN_CONTENT_MANAGER: 'admin_content_manager',
    ADMIN_COMMS_MANAGER: 'admin_comms_manager',
    ADMIN_AI_MANAGER: 'admin_ai_manager',
    ADMIN_SYSTEM_OPS: 'admin_system_ops',
    // Read-only cross-org ops debugging role (ops-read MCP) — grantable to on-call
    // engineers without super_admin
    ADMIN_OPS_READER: 'admin_ops_reader',
    // Support session management role
    ADMIN_SUPPORT_MANAGER: 'admin_support_manager',
    // Support user account role (identifies support staff who can be granted temp access to orgs)
    ADMIN_SUPPORT_USER: 'admin_support_user',
} as const;

export const ALL_ROLES = {
    ...ORG_ROLES,
    ...ADMIN_ROLES,
} as const;

export const SYSTEM_ACCOUNT_IDS = {
    SYSTEM: 1n,
    OTHER: -1n,
    ANONYMOUS: -2n,
    /**
     * Clara, the claims agent (CUR-1158). A real account with a membership in every org so
     * its automatic writes are attributed to "Clara" in History and are filterable, rather
     * than hiding behind the bare System account. Never a login; never assignable.
     */
    CLARA: -3n,
    /*
        Unallocated user id is store in DB as null. However null is not a valid value for Select component. So we use -9999 as a placeholder for null.
    */
    UNALLOCATED: -9999n,
    /*
        "Current user" filter sentinel — a saved view / filter can reference the *viewing* user
        dynamically (so a shared "My items" view resolves per-person). It is never a real account
        id and is never persisted to a row; the filter layer swaps it for the live user's id when
        building the where clause.
    */
    CURRENT_USER: -9998n,
} as const;

export const USER_SYSTEM_USAGES = {
    '1st_line': '1st Line',
    '2nd_line': '2nd Line',
    audit: 'Audit',
    oversight: 'Oversight',
    non_contributory: 'Non-contributory',
} as const;

export const USER_SYSTEM_USAGE_KEYS = mapObjectKeysToValues(USER_SYSTEM_USAGES);

export const USER_STATISTIC_SETTINGS = {
    GREEN_STATUS_OF_NUMBER_OF_LOGINS_IN_12_WEEKS_PERCENTAGE_STEPS: [25, 74.99],
    FIRST_LINE_NUMBER_OF_LOGINS_IN_12_WEEKS_STEPS: [3, 11],
    SECOND_LINE_NUMBER_OF_LOGINS_IN_12_WEEKS_STEPS: [3, 11],
    OVERSIGHT_NUMBER_OF_LOGINS_IN_12_WEEKS_STEPS: [0, 2],
} as const;

export type UserFlag = (typeof USER_FLAGS)[keyof typeof USER_FLAGS];
export type OrgRole = (typeof ORG_ROLES)[keyof typeof ORG_ROLES];
export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];
export type Role = OrgRole | AdminRole;
export type SystemAccountId = keyof typeof SYSTEM_ACCOUNT_IDS;
export type UserSystemUsage = keyof typeof USER_SYSTEM_USAGES;

/** Roles that indicate the user actively uses the core system (claims, incidents, etc.) */
export const CORE_SYSTEM_ROLES = [
    ORG_ROLES.ORG_ADMIN,
    ORG_ROLES.USER_MANAGER,
    ORG_ROLES.USER,
    ORG_ROLES.SHARED_EDIT_USER,
] as const;
